"use client";

import { useQuickJoinStore } from "modules/lgs/hooks/useQuickJoinStore";
import { LgsLogo } from "modules/lgs/components/LgsLogo";
import type { LGS } from "modules/lgs/model/lgs";
import type { ShortLGS } from "modules/common/model/models";
import { cn } from "utils/utils";

interface JoinStoreButtonProps {
  store: LGS | ShortLGS;
  storeName: string;
  className?: string;
}

/**
 * Client component that wraps useQuickJoinStore for server-rendered parents
 * (like StoreLockedBanner) that can't use hooks directly.
 */
export function JoinStoreButton({
  store,
  storeName,
  className,
}: JoinStoreButtonProps) {
  const { joinStore, isJoining, isMember } = useQuickJoinStore(store.wotcId, {
    storeCoords: [store.longitude, store.latitude],
  });

  if (isMember) return null;

  return (
    <button
      type="button"
      onClick={() => joinStore(store)}
      disabled={isJoining}
      className={cn(
        "hover:bg-base-300 group flex w-full items-center gap-3 rounded-lg p-2 transition-colors",
        className
      )}
    >
      <div className="bg-primary/15 group-hover:bg-primary/25 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg transition-colors">
        <LgsLogo
          wotcId={store.wotcId}
          storeName={storeName}
          isTradingHub={store.isTradingHub}
          size="sm"
          fallbackClassName="text-primary"
        />
      </div>
      <div className="min-w-0 flex-1 text-left">
        <p className="text-base-content text-sm font-medium">
          {isJoining ? "Joining..." : `Join ${storeName}`}
        </p>
        <p className="text-base-content/60 text-xs">
          Add this store to your profile to start trading
        </p>
      </div>
      {isJoining && (
        <span className="loading loading-spinner loading-xs text-primary flex-shrink-0" />
      )}
    </button>
  );
}
